import { useCallback, useRef, useState, useEffect } from "react";
import { LocateFixed, Search as SearchIcon, Plus, Info } from "lucide-react";
import api from "../lib/api";
import { ListRow, Field, SecondaryAction, ActionButton } from "./kit";
import { useDirectorySearch } from "../hooks/useDirectorySearch";
import { markNav, track } from "../lib/usageTracker";
import { readViewportGrounds } from "../lib/mapViewport";
import { resolveGroundById } from "../lib/directoryLoad";
import LogGroundMapBand from "./LogGroundMapBand";
import {
  NEAR_ME_BUSY_LABEL,
  NEAR_ME_LABEL,
  NEAR_ME_RADIUS_KM,
  DIRECTORY_UNAVAILABLE_MESSAGE,
  findNearMe,
} from "../lib/nearMe";
import {
  LOG_STEP_EYEBROW,
  LOG_STEP_TITLE,
  LOG_STEP_LEDE,
  LOG_STEP_LEDE_TWO,
  LOG_STEP_BACK_LABEL,
  WAY_1_LABEL,
  WAY_1_HELPER,
  WAY_1_HELPER_SCHEDULE,
  SCHEDULE_STEP_EYEBROW,
  SCHEDULE_STEP_TITLE,
  PLAN_STEP_EYEBROW,
  PLAN_STEP_LEDE,
  PLAN_STEP_CLOSING_LINE,
  WAY_2_LABEL,
  WAY_2_HELPER_PREFIX,
  WAY_3_LABEL,
  WAY_3_HELPER,
  WAY_3_HELPER_TWO,
  LOG_STEP_SEARCH_PLACEHOLDER,
  LOG_STEP_CLOSING_LINE,
  LOG_STEP_CLOSING_LINE_TWO,
  mergeSavedAndPlayed,
  chooseMapRows,
  playedAtLabel,
} from "../lib/logGroundStep";

const NEAR_ME_IDLE = { status: "idle", results: [], message: "", position: null };

/** The numbered badge each of the three ways in carries — `DirectionA1.dc.html:62`
 * draws this at 20px (issue #757 §2). */
function NumberBadge({ n }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 20,
        height: 20,
        borderRadius: "var(--nw-radius-pill)",
        background: "var(--nw-navy)",
        color: "#FFFFFF",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        fontSize: 11,
        lineHeight: 1,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {n}
    </span>
  );
}

function WayHeading({ n, children }) {
  return (
    <div className="flex items-center" style={{ gap: "var(--nw-space-grid)" }}>
      <NumberBadge n={n} />
      {/* `.nw-section` (index.css) declares no explicit line-height —
          harmless where every existing call site sits inside its own
          isolated box, but THIS one is a direct page-stack child, so an
          inherited rem-derived line-height would move the STACK GAP below
          it with the text-size control (watched failing round 1: 13.38/
          13.56/13.7px at Compact/Default/Large). `1.4` matches the kit's
          own small-label rhythm and, being unitless, resolves against this
          span's own fixed 10px font-size regardless of the root's. */}
      <span className="nw-section" style={{ lineHeight: 1.4 }}>{children}</span>
    </div>
  );
}

/**
 * Issue #757 review round 2 — step 1 of 3, "Where did you play?", matched
 * against `DirectionA1.dc.html` at `design-baseline-round-4` (B7): the
 * content stack is `gap: 16px; padding: 24px 20px` (`.nw-stack-log`,
 * `--nw-space-log-stack`/`--nw-space-log-page` — this screen's own drawn
 * numbers, distinct from the other rebuilt screens' 14px), each numbered
 * way is its own `gap: 8px` column (`.nw-stack-grid`), "Use my location"
 * is the screen's ONE gold element (R-003 — the drawing, not the issue
 * outline, wins where the two read differently; the add panel's own gold
 * element, "Add it from where I am", is a SEPARATE view), the search
 * placeholder is "Type a ground or a town", the map band is PERMANENT
 * (visible on arrival, `LogGroundMapBand.jsx` — B3), and the closing line
 * is a bordered info card, not a bare paragraph.
 *
 * `future` (D394) — the game has not happened (Plan a match): the future-tense
 * wording of D380, with the saved list kept. Default false: the log step is
 * unchanged.
 *
 * `onPicked(name, groundId)` — a ground chosen from ANY of the three ways
 * (or the map). `onAddGround(seed)` — routes to `LogMatchAddGroundPanel`,
 * seeded with whatever was typed in the search box, if anything.
 */
export default function LogMatchGroundStep({ presetGround, onPicked, onAddGround, onExitStep1, showSaved = true, future = false }) {
  // ---------- Section 1: Use my location ----------
  const [nearMe, setNearMe] = useState(NEAR_ME_IDLE);
  const directoryRef = useRef(null);

  // Issue #134's own privacy shape, reused rather than re-derived: the
  // directory is fetched (and cached) BEFORE any position is read, so
  // nothing here can carry a position into the request that produced the
  // list. Duplicated from `GroundPicker.jsx`'s own `loadDirectory` rather
  // than imported — that file's near-me implementation is pinned verbatim
  // by `GroundPicker.test.js`'s source-level assertions, and this screen's
  // rendering is deliberately not that component's dropdown, so sharing the
  // one internal helper would mean exporting it from a file whose contract
  // is "a picker", not "a directory loader".
  const loadDirectory = useCallback(async () => {
    if (directoryRef.current) return directoryRef.current;
    try {
      const r = await api.get("/grounds");
      directoryRef.current = readViewportGrounds(r?.data).grounds;
      return directoryRef.current;
    } catch {
      return null;
    }
  }, []);

  // Issue #766 ruling 4 (D167) — "Log a match here" (`GroundCard.jsx`)
  // carries a ground hint beside `?new=1` (`lib/activityCreate.js`'s
  // `withCreate`). `presetGround` is a PROP now, not this component's own
  // `useSearchParams()` read — PR #921 review (F3): a sibling effect here
  // reading and stripping the URL raced `useLogFlowStep`'s own `?step=`
  // sync effect and `useCreateDialog`'s `?new=1` strip, all three building
  // their own `next` from the same stale snapshot in one commit, so the
  // strip kept coming back. `useLogFlowStep.js` is the one place that
  // reads and spends it now; this component only ever sees the resolved
  // value, once, as whatever that hook's own state currently holds.
  //
  // PR #921 review (F1) — resolved through `resolveGroundById`
  // (`lib/directoryLoad.js`), never the unbounded `loadDirectory()` above:
  // that read is capped at 1,000 rows ordered by id, so a ground created
  // after the 1,000th would silently "not exist" to a `.find()` over it,
  // exactly the bug a bbox-or-search resolve exists to avoid.
  useEffect(() => {
    if (!presetGround?.groundId) return;
    let cancelled = false;
    resolveGroundById(api, presetGround.groundId, {
      latitude: presetGround.latitude,
      longitude: presetGround.longitude,
      name: presetGround.name,
    }).then((g) => {
      if (cancelled || !g) return;
      onPicked(g.name, g.ground_id);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetGround?.groundId]);

  const handleNearMe = async () => {
    track("log.near_me");
    setNearMe({ ...NEAR_ME_IDLE, status: "locating" });
    const directory = await loadDirectory();
    if (directory === null) {
      setNearMe({ ...NEAR_ME_IDLE, status: "error", message: DIRECTORY_UNAVAILABLE_MESSAGE });
      return;
    }
    const outcome = await findNearMe(directory);
    if (!outcome.ok) {
      setNearMe({ ...NEAR_ME_IDLE, status: "error", message: outcome.message });
      return;
    }
    setNearMe({ status: "ready", results: outcome.results, message: "", position: outcome.position });
  };

  // ---------- Section 2: Saved, or played at ----------
  const [savedGrounds, setSavedGrounds] = useState([]);
  const [venuesPlayed, setVenuesPlayed] = useState([]);
  // #1610: the first frame waits for BOTH lists (#1494), so the second one landing never re-frames a panned map.
  const [listsLoaded, setListsLoaded] = useState({ saved: false, played: false });
  // D367.11 (#1325): a coach has no player profile, so both calls fail for
  // them. `showSaved={false}` (Schedule a game) hides the section and makes
  // neither request; every other caller keeps the default.
  useEffect(() => {
    if (!showSaved) return;
    const settle = (key) => setListsLoaded((prev) => ({ ...prev, [key]: true }));
    api.get("/players/me/grounds").then((r) => setSavedGrounds(r.data || [])).catch(() => setSavedGrounds([])).finally(() => settle("saved"));
    api.get("/venues/played").then((r) => setVenuesPlayed(r.data || [])).catch(() => setVenuesPlayed([])).finally(() => settle("played"));
  }, [showSaved]);
  const savedOrPlayed = mergeSavedAndPlayed(savedGrounds, venuesPlayed);

  // ---------- Section 3: Search the world directory ----------
  const [q, setQ] = useState("");
  // The first keystroke is counted once per mount; the text is never read.
  const searchedRef = useRef(false);
  const search = useDirectorySearch(api, q, false);
  const searchRows = search.rows.slice(0, 8);
  const typed = q.trim();
  const searchNoMatch = typed.length > 0 && !search.pending && search.rows.length === 0;

  // B3 — the map band is permanent (visible on arrival, not gated on a
  // search): it plots the current search rows when there are any, and
  // falls back to an NZ-centred view otherwise (R3 non-blocking (g),
  // `LogGroundMapBand.jsx`'s own `NZ_FALLBACK_CENTER`) — never absent.
  // #1610 — a typed search wins, then the near-me ranking, then the saved or played grounds.
  const map = chooseMapRows({ typed, searchRows, nearMe, savedOrPlayed });
  // The framing question: what the map is answering, never how big the answer is (#643).
  // Search: the term the rows ON SCREEN answer (`answeredTerm`, #644), not the one in the box, so the reason changes
  // when the answer does. Near me: the position, held while a re-locate is in flight so the map does not
  // fly to the saved pins and back. Saved or played: one constant reason.
  const lastNearReason = useRef(null);
  let mapReason = "saved";
  if (map.source === "search") mapReason = `search:${search.answeredTerm}`;
  else if (map.source === "near") {
    mapReason = `near-me:${nearMe.position?.lat},${nearMe.position?.lng}`;
    lastNearReason.current = mapReason;
  } else if (nearMe.status === "locating" && lastNearReason.current) mapReason = lastNearReason.current;
  const holdFirst = map.source === "saved" && showSaved && !(listsLoaded.saved && listsLoaded.played);

  return (
    <div className="nw-stack-log" data-testid="log-step-1-content" style={{ padding: "var(--nw-space-log-page)" }}>
      <span data-testid="log-step-1" className="sr-only" aria-hidden="true" />

      {/* Review round 2, R1 (Andreas's decision, 2026-09-24) — the step's
          own way out. Not in the TopBar's logo slot (that's the app mark,
          not a control); this is the first element of the step's own
          content, below the bar, a plain text link at kit `type.help`
          size. Clears the whole flow (`onExitStep1`, wired in
          `PerformanceTracking.jsx` to the same `closeDialog(false)` the
          old close-X used) rather than merely stepping back within it —
          the add panel's own "Back to the list" (below) is the
          one-level-less-far version of this. */}
      {/* Round 5 conformance — DirectionA1.dc.html:60 draws this underlined
          (`text-decoration: underline`), font-size 12px, `#64748B`. */}
      <SecondaryAction
        variant="link"
        onClick={() => {
          markNav("back");
          onExitStep1();
        }}
        data-testid="log-step-1-back"
        style={{
          // Block-level, as the old `display: block` button was, so the
          // box sits in the stack's flow and its footprint stays one line.
          display: "flex",
          width: "fit-content",
          justifyContent: "flex-start",
          textAlign: "left",
          alignSelf: "flex-start",
          textDecoration: "underline",
          fontWeight: 400,
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--nw-ink-muted)",
        }}
      >
        {LOG_STEP_BACK_LABEL}
      </SecondaryAction>

      <div className="nw-stack-tight">
        <div className="nw-eyebrow">{future ? PLAN_STEP_EYEBROW : showSaved ? LOG_STEP_EYEBROW : SCHEDULE_STEP_EYEBROW}</div>
        <h1 className="nw-title-lg">{future || !showSaved ? SCHEDULE_STEP_TITLE : LOG_STEP_TITLE}</h1>
        <p className="nw-lede">{future ? PLAN_STEP_LEDE : showSaved ? LOG_STEP_LEDE : LOG_STEP_LEDE_TWO}</p>
      </div>

      {/* Way 1 — Use my location. The screen's ONE gold element (R-003). */}
      <div className="nw-stack-grid">
        <WayHeading n={1}>{WAY_1_LABEL}</WayHeading>
        {/* Round 5 conformance, D27 (issue #864) — the gold action's label
            and icon default to near-black ink in the kit component itself
            now; DirectionA1.dc.html:72-74 draws this button that way. No
            per-call override needed here any more. */}
        <ActionButton
          onClick={handleNearMe}
          disabled={nearMe.status === "locating"}
          data-testid="log-near-me"
          icon={<LocateFixed size={20} />}
        >
          {nearMe.status === "locating" ? NEAR_ME_BUSY_LABEL : NEAR_ME_LABEL}
        </ActionButton>
        <p className="nw-help">{future || !showSaved ? WAY_1_HELPER_SCHEDULE : WAY_1_HELPER}</p>

        {nearMe.status === "error" && (
          <p className="nw-help" role="status" aria-live="polite" data-testid="log-near-me-message">
            {nearMe.message}
          </p>
        )}

        {nearMe.status === "ready" && (
          <div className="nw-stack-grid" data-testid="log-near-me-results">
            {nearMe.results.length > 0 ? (
              nearMe.results.map((g) => (
                <ListRow
                  key={g.ground_id || g.name}
                  data-testid={`log-near-option-${g.name}`}
                  name={g.name}
                  figuresLine={g._distanceLabel}
                  onClick={() => onPicked(g.name, g.ground_id)}
                  style={{ cursor: "pointer" }}
                  chevron
                />
              ))
            ) : (
              <div data-testid="log-near-me-empty">
                <p className="nw-help">No known grounds within {NEAR_ME_RADIUS_KM} km of you.</p>
                <SecondaryAction variant="link" onClick={() => onAddGround("")} data-testid="log-near-me-add">
                  Add this place
                </SecondaryAction>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Way 2 — Saved, or played at. `log-saved-list` is DirectionA1.dc.html's
          own id on this list (round 5 conformance) — added on the
          container; each row keeps its own `log-saved-option-<name>` too,
          since the drawing names one representative row but the journeys
          already rely on a per-row id to pick a specific ground. */}
      {showSaved && (
      <div className="nw-stack-grid" data-testid="log-saved-list">
        <WayHeading n={2}>{WAY_2_LABEL}</WayHeading>
        {savedOrPlayed.map((g) => (
          <ListRow
            key={g.groundId}
            data-testid={`log-saved-option-${g.name}`}
            name={g.name}
            figuresLine={playedAtLabel(g.matches, g.lastPlayed)}
            onClick={() => onPicked(g.name, g.groundId)}
            style={{ cursor: "pointer" }}
            chevron
          />
        ))}
        <p className="nw-help">
          {WAY_2_HELPER_PREFIX} {savedOrPlayed.length} in the list.
        </p>
      </div>
      )}

      {/* Way 3 — Search the world directory (+ the permanent map band) */}
      <div className="nw-stack-grid">
        <WayHeading n={showSaved ? 3 : 2}>{WAY_3_LABEL}</WayHeading>
        {/* Review round 3 (#907, F1): the drawing has no visible label
            above this field, only the placeholder — an `aria-label`
            carries the same text as an accessible name without adding a
            new visible element (which would break the byte-identical
            requirement). */}
        <Field
          data-testid="log-search"
          placeholder={LOG_STEP_SEARCH_PLACEHOLDER}
          value={q}
          onChange={(e) => {
            if (!searchedRef.current) {
              searchedRef.current = true;
              track("log.search");
            }
            setQ(e.target.value);
          }}
          aria-label={LOG_STEP_SEARCH_PLACEHOLDER}
        />

        {typed.length > 0 && (
          <div className="nw-stack-grid" data-testid="log-search-results">
            {searchNoMatch ? (
              <div data-testid="log-search-no-match">
                <SecondaryAction
                  variant="link"
                  onClick={() => onAddGround(typed)}
                  data-testid="log-search-add-new"
                  // D237: grows down only; pulled up it would cover the bottom of
                  // the search field above, so the label sits one half-leading lower.
                  style={{ marginTop: 0 }}
                >
                  <Plus size={14} style={{ marginRight: 6 }} aria-hidden="true" />
                  {`Add "${typed}"`}
                </SecondaryAction>
              </div>
            ) : (
              searchRows.map((g) => (
                <ListRow
                  key={g.ground_id}
                  data-testid={`log-search-option-${g.name}`}
                  name={g.name}
                  figuresLine={[g.city, g.country].filter(Boolean).join(", ") || null}
                  onClick={() => onPicked(g.name, g.ground_id)}
                  style={{ cursor: "pointer" }}
                  chevron
                />
              ))
            )}
            {search.answer && search.answer.matching > searchRows.length && (
              <p className="nw-help" data-testid="log-search-more-note">
                Showing {searchRows.length} of {search.answer.matching} — keep typing to narrow.
              </p>
            )}
          </div>
        )}

        {/* B3 — SHIPPED map behaviour (satellite/street toggle, `nw_basemap`
            preference), visible on arrival, never a link away to a
            different page mid-log. */}
        <LogGroundMapBand
          rows={map.rows}
          onPick={onPicked}
          fit
          fitReason={mapReason}
          holdFirstArrival={holdFirst}
          centre={map.source === "near" ? nearMe.position : undefined}
        />

        <p className="nw-help">{showSaved ? WAY_3_HELPER : WAY_3_HELPER_TWO}</p>
      </div>

      {/* `DirectionA1.dc.html:109` draws the closing line as a bordered
          info card, not a bare paragraph. */}
      <div
        className="flex items-start"
        style={{
          gap: "var(--nw-space-grid)",
          padding: "12px 14px",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-control)",
        }}
      >
        <Info size={16} style={{ color: "var(--nw-ink-muted)", marginTop: 2, flexShrink: 0 }} aria-hidden="true" />
        <p className="nw-help" data-testid="log-step-1-closing-line">
          {future ? PLAN_STEP_CLOSING_LINE : showSaved ? LOG_STEP_CLOSING_LINE : LOG_STEP_CLOSING_LINE_TWO}
        </p>
      </div>
    </div>
  );
}
